import { $severity } from '../constants/accessibility';
import { ApiQueryOption } from '../constants/api';
import { AccessibilityAuditToolNames, AuditToolNames } from '../constants/audit-tool';
import {
  $bulkTaskCreationInfo,
  $finding,
  $findings,
  DigitalAssetFindingsFilterQueryOptions,
  DigitalAssetFindingsSortBy,
  FilterRecordEvent,
} from '../constants/finding';
import { flawStatus } from '../constants/flaw';
import { $sortingOrder } from '../constants/sort';
import { QueryBoolean } from './common.interface';
import { IFlaw } from './flaw.interface';
import { ITask } from './task.interface';
import { IAuditStandardSuccessCriteria } from './audit-standard-success-criteria.interface';
import { IFindingUrlShortLinkContext } from './short-link.interface';
import { scanningType } from '../constants/scanning';
import { PrioritizationFilterOptions } from './conformance-score.interface';
import { $task, $taskFlaw } from '../constants/task';

export interface IContentsquareIntegrationMetrics {
  pageViews?: number;
  bounceRate?: number;
}

export interface IFinding {
  [$finding._id]: string;
  [$finding.findingId]: string;
  [$finding.url]: string;
  [$finding.pageTitle]: string;
  [$finding.pageId]: string;
  [$finding.createdOn]: Date;
  [$finding.severity]: $severity;
  [$finding.status]: flawStatus;
  [$finding.task]?: ITask;
  [$finding.ruleId]: string;
  [$finding.ruleTitle]: string;
  [$finding.flawOccurrence]: number;
  [$finding.toolId]: IFlaw['toolId'];
  [$finding.firstFound]: IFlaw['firstFound'];
  [$finding.lastFound]: IFlaw['lastFound'];
  [$finding.statusHistory]: IFlaw['statusHistory'];
  [$finding.data]: IFlaw['data'];
  [$finding.auditedDocumentId]?: string;
  [$finding.auditedDocumentPageId]?: string;
  [$finding.scanningType]: scanningType;
  [$finding.contentsquareIntegration]?: IContentsquareIntegrationMetrics;
}

export interface IFindingResponse {
  [$findings.findings]: IFinding[];
  [$findings._total]: number;
}

export interface ITaskLinkedFlaw {
  [$finding._id]: string;
  [$finding.findingId]: string;
}

export interface ITaskInfo {
  [$finding.ruleId]: string;
  auditRuleId: string;
  [$task.name]: string;
  [$task.description]: string;
  [$taskFlaw.flaws]: ITaskLinkedFlaw[];
}

export interface IBulkTaskCreationInfoResponse {
  [$bulkTaskCreationInfo.tasks]: ITaskInfo[];
  [$bulkTaskCreationInfo._totalFlaws]: number;
  [$bulkTaskCreationInfo._totalTasks]: number;
}

export enum TaskLinkingStatus {
  linked = 'linked',
  unlinked = 'unlinked',
}

export enum RecordEventType {
  none = 'none',
  aiFilter = 'aiFilter',
  manualFilter = 'manualFilter',
}

export interface RecordEvent {
  [FilterRecordEvent.recordEventType]?: RecordEventType;
  [FilterRecordEvent.recordEventAiFilterMessage]?: string;
}

export interface IFindingFilterOptions {
  [DigitalAssetFindingsFilterQueryOptions.findingId]?: string[];
  [DigitalAssetFindingsFilterQueryOptions.ruleId]?: string;
  [DigitalAssetFindingsFilterQueryOptions.ruleTitle]?: string[];
  [DigitalAssetFindingsFilterQueryOptions.pageUrl]?: string[];
  [DigitalAssetFindingsFilterQueryOptions.taskProjectId]?: string[];
  [DigitalAssetFindingsFilterQueryOptions.severities]?: $severity[];
  [DigitalAssetFindingsFilterQueryOptions.statuses]?: flawStatus[];
  [DigitalAssetFindingsFilterQueryOptions.tool]?: AuditToolNames;
  [DigitalAssetFindingsFilterQueryOptions.successCriteria]?: string[];
  [DigitalAssetFindingsFilterQueryOptions.hasTask]?: QueryBoolean;
  [DigitalAssetFindingsFilterQueryOptions.prioritizationMethod]?: PrioritizationFilterOptions;
}

export interface IGetDigitalAssetFindingsRequestQuery extends IFindingFilterOptions, RecordEvent {
  [ApiQueryOption.page]?: string;
  [ApiQueryOption.skip]?: string;
  [ApiQueryOption.limit]?: string;
  [ApiQueryOption.sortBy]?: DigitalAssetFindingsSortBy;
  [ApiQueryOption.direction]?: $sortingOrder.asc | $sortingOrder.desc;
}

export interface IGetFindingRulesRequestQuery {
  [ApiQueryOption.tool]: AuditToolNames;
}

export type IGetBulkTaskCreationInfoRequestQuery = Omit<IFindingFilterOptions, 'taskProjectId' | 'statuses' | 'hasTask'>;

export interface IGetFindingsReportByRuleRequest {
  format: string;
  auditTool?: AccessibilityAuditToolNames;
  flawIds: string[];
  taskProjectId?: string;
}

export interface IFlawFinding {
  issueId: string;
  ruleId: string;
  ruleTitle: string;
  severity: $severity;
  successCriterias: IAuditStandardSuccessCriteria[];
  en301549SuccessCriterias?: IAuditStandardSuccessCriteria[];
  section508SuccessCriterias?: IAuditStandardSuccessCriteria[];
}

export interface IFlawFindingCountBySuccessCriteria {
  successCriteria: IAuditStandardSuccessCriteria;
  totalFindings: number;
}

export interface IFindingUrlContext extends IFindingUrlShortLinkContext {
  auditedDocumentId?: string;
  pageId?: string;
}
